import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, DollarSign, Target, ArrowUpCircle, ArrowDownCircle, PieChart as PieChartIcon } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function Reports({ user, onLogout }) {
  const [reports, setReports] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('this-month');

  useEffect(() => {
    fetchReports();
  }, [period]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [reportsRes, transactionsRes, goalsRes] = await Promise.all([
        axios.get(`${API}/reports?period=${period}`, { headers }),
        axios.get(`${API}/transactions`, { headers }),
        axios.get(`${API}/goals`, { headers })
      ]);
      
      setReports(reportsRes.data);
      setTransactions(transactionsRes.data);
      setGoals(goalsRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <Navigation user={user} onLogout={onLogout} currentPage="/reports" />
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }

  const expenseData = reports?.expense_by_category
    ? Object.entries(reports.expense_by_category).map(([category, amount]) => ({
        name: category,
        value: amount
      }))
    : [];

  const totalExpenses = expenseData.reduce((sum, item) => sum + item.value, 0);
  const savingsRate = reports?.total_income > 0 ? (reports.net_savings / reports.total_income * 100) : 0;
  const topSpending = [...expenseData].sort((a, b) => b.value - a.value).slice(0, 5);

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/reports" />

        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">Financial Reports & Analytics</h1>
              <p className="text-gray-600 dark:text-gray-400">Comprehensive view of your financial health</p>
            </div>
            <div className="mt-4 md:mt-0">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[180px] dark:bg-gray-800 dark:text-gray-100">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this-month">This Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="all-time">All Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="glass p-6 dark:bg-gray-800">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Income</h3>
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(reports?.total_income || 0, user?.currency)}</p>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center mb-4">
              <TrendingDown className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Expenses</h3>
            <p className="text-3xl font-bold text-rose-600 dark:text-rose-400">{formatCurrency(reports?.total_expenses || 0, user?.currency)}</p>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Net Savings</h3>
            <p className={`text-3xl font-bold ${reports?.net_savings >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {formatCurrency(reports?.net_savings || 0, user?.currency)}
            </p>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Savings Rate</h3>
            <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">{savingsRate.toFixed(1)}%</p>
          </Card>
        </div>

        {/* Income vs Expenses & Expense Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Income vs Expenses */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Income vs Expenses</h2>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center space-x-2">
                    <ArrowUpCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Income</span>
                  </div>
                  <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(reports?.total_income || 0, user?.currency)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                  <div className="bg-gradient-to-r from-emerald-400 to-teal-500 h-4 rounded-full" style={{ width: '100%' }}></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center space-x-2">
                    <ArrowDownCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Expenses</span>
                  </div>
                  <span className="text-xl font-bold text-rose-600 dark:text-rose-400">
                    {formatCurrency(reports?.total_expenses || 0, user?.currency)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                  <div 
                    className="bg-gradient-to-r from-rose-400 to-pink-500 h-4 rounded-full"
                    style={{ width: reports?.total_income > 0 ? `${(reports.total_expenses / reports.total_income * 100)}%` : '0%' }}
                  ></div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 p-4 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">Net Difference</span>
                  <span className={`text-2xl font-bold ${reports?.net_savings >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {reports?.net_savings >= 0 ? '+' : ''}{formatCurrency(reports?.net_savings || 0, user?.currency)}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Expense Breakdown */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Expense Breakdown</h2>
            {expenseData.length > 0 ? (
              <div className="space-y-3">
                {expenseData.map((item, index) => {
                  const percentage = (item.value / totalExpenses * 100).toFixed(1);
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{formatCurrency(item.value, user?.currency)}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({percentage}%)</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%`, backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">No expense data available</p>
            )}
          </Card>
        </div>

        {/* Top Spending Categories */}
        {topSpending.length > 0 && (
          <Card className="glass p-6 mb-8 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Top Spending Categories</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {topSpending.map((category, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 p-4 rounded-xl border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-2">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: COLORS[index % COLORS.length] + '20' }}
                    >
                      <PieChartIcon className="w-4 h-4" style={{ color: COLORS[index % COLORS.length] }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</p>
                      <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{category.name}</p>
                    </div>
                  </div>
                  <p className="text-lg font-bold" style={{ color: COLORS[index % COLORS.length] }}>
                    {formatCurrency(category.value, user?.currency)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {((category.value / totalExpenses) * 100).toFixed(1)}% of total
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Financial Health Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="glass p-6 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Investment Performance</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Invested</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{formatCurrency(reports?.total_investment || 0, user?.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Current Value</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{formatCurrency(reports?.total_current_value || 0, user?.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Returns</span>
                <span className={`font-bold ${reports?.investment_return >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {reports?.investment_return >= 0 ? '+' : ''}{formatCurrency(reports?.investment_return || 0, user?.currency)}
                </span>
              </div>
            </div>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Goals Progress</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Active Goals</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{goals.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Target</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {formatCurrency(goals.reduce((sum, g) => sum + g.target_amount, 0), user?.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Achieved</span>
                <span className="font-semibold text-purple-600 dark:text-purple-400">
                  {formatCurrency(goals.reduce((sum, g) => sum + g.current_amount, 0), user?.currency)}
                </span>
              </div>
            </div>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Transaction Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Transactions</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{transactions.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Income Count</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">{transactions.filter(t => t.type === 'income').length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Expense Count</span>
                <span className="font-semibold text-rose-600 dark:text-rose-400">{transactions.filter(t => t.type === 'expense').length}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Financial Insights */}
        <Card className="glass p-6 dark:bg-gray-800">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Financial Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900 dark:to-teal-900 p-4 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Savings Rate</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{savingsRate.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Of total income</p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 p-4 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Expense Ratio</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {reports?.total_income > 0 ? ((reports.total_expenses / reports.total_income) * 100).toFixed(1) : 0}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Expense to income</p>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900 dark:to-pink-900 p-4 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Investment Growth</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {reports?.total_investment > 0 ? ((reports.investment_return / reports.total_investment) * 100).toFixed(1) : 0}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Return on investment</p>
            </div>
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900 dark:to-orange-900 p-4 rounded-xl">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Active Goals</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{goals.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Financial goals</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
